/**
 * Popper z akcjami filtrów: eksport CSV, układ i widoczność kolumn, wyrażenie filtrujące, zapisz układ.
 * Ikona Gear w toolbarze otwiera ten popper zamiast od razu AdvancedFiltersDialog.
 */

import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Popper,
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ClickAwayListener
} from '@mui/material';
import { FileEarmarkSpreadsheet, LayoutThreeColumns, Funnel, Sliders, Save } from 'react-bootstrap-icons';

import styles from 'theme/components/wise/WiseTable/components/FiltersActionPopper/FiltersActionPopper.module.scss';

export interface IFiltersActionPopperProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  /** Otwiera AdvancedFiltersDialog w zakładce „Widoczność kolumn” */
  onOpenColumnVisibility: () => void;
  /** Otwiera AdvancedFiltersDialog w zakładce „Zaawansowane filtry” */
  onOpenAdvancedFilters: () => void;
  /** Otwiera AdvancedFiltersDialog w zakładce „Zapisane ustawienia” */
  onOpenSavedSettings: () => void;
  onExportCsv?: () => void;
  onSaveCurrentLayout?: () => void;
}

const FiltersActionPopper: FC<IFiltersActionPopperProps> = ({
  open,
  anchorEl,
  onClose,
  onOpenColumnVisibility,
  onOpenAdvancedFilters,
  onOpenSavedSettings,
  onExportCsv,
  onSaveCurrentLayout
}) => {
  const { t } = useTranslation();

  const handleColumnVisibility = () => {
    onClose();
    onOpenColumnVisibility();
  };

  const handleAdvancedFilters = () => {
    onClose();
    onOpenAdvancedFilters();
  };

  const handleSavedSettings = () => {
    onClose();
    onOpenSavedSettings();
  };

  const handleExportCsv = () => {
    onClose();
    onExportCsv?.();
  };

  const handleSaveLayout = () => {
    onClose();
    onSaveCurrentLayout?.();
  };

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      modifiers={[
        { name: 'offset', options: { offset: [0, 8] } },
        { name: 'flip', options: { fallbackPlacements: ['bottom-end', 'top-start'] } }
      ]}
      sx={{ zIndex: 1300 }}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          elevation={8}
          className={styles.paper}
          sx={{ minWidth: 240, py: 0 }}
        >
          <List disablePadding>
          <ListItemButton onClick={handleExportCsv} className={styles.item}>
            <ListItemIcon className={styles.icon}>
              <FileEarmarkSpreadsheet size={18} />
            </ListItemIcon>
            <ListItemText primary={t('Eksport CSV')} />
          </ListItemButton>
          <ListItemButton onClick={handleColumnVisibility} className={styles.item}>
            <ListItemIcon className={styles.icon}>
              <LayoutThreeColumns size={18} />
            </ListItemIcon>
            <ListItemText primary={t('Układ i widoczność kolumn')} />
          </ListItemButton>
          <ListItemButton onClick={handleAdvancedFilters} className={styles.item}>
            <ListItemIcon className={styles.icon}>
              <Funnel size={18} />
            </ListItemIcon>
            <ListItemText primary={t('Wyrażenie filtrujące')} />
          </ListItemButton>
          <ListItemButton onClick={handleSavedSettings} className={styles.item}>
            <ListItemIcon className={styles.icon}>
              <Sliders size={18} />
            </ListItemIcon>
            <ListItemText primary={t('Wybierz ustawienia')} />
          </ListItemButton>
          <ListItemButton onClick={handleSaveLayout} className={styles.item}>
            <ListItemIcon className={styles.icon}>
              <Save size={18} />
            </ListItemIcon>
            <ListItemText primary={t('Zapisz bieżący układ')} />
          </ListItemButton>
        </List>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

export default FiltersActionPopper;
